import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthPage from "@/pages/AuthPage";
import GoogleCallback from "@/components/GoogleCallback";
import Landing from "@/pages/Landing";
import Onboarding from "@/pages/Onboarding";
import Layout from "@/components/Layout";
import Overview from "@/pages/Overview";
import Campaigns from "@/pages/Campaigns";
import Audiences from "@/pages/Audiences";
import Ads from "@/pages/Ads";
import TeamMembers from "@/pages/TeamMembers";
import ActivityLog from "@/pages/ActivityLog";
import Leads from "@/pages/Leads";
import LeadForms from "@/pages/LeadForms";
import LeadAssessment from "@/pages/LeadAssessment";
import ConversionReport from "@/pages/ConversionReport";
import Integrations from "@/pages/Integrations";
import Connect from "@/pages/Connect";
import Automation from "@/pages/Automation";
import Insights from "@/pages/Insights";
import Team from "@/pages/Team";
import Brand from "@/pages/Brand";
import Billing from "@/pages/Billing";
import Legal from "@/pages/Legal";
import AdminPortal from "@/pages/AdminPortal";
import Support from "@/pages/Support";
import Roadmap from "@/pages/Roadmap";

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#FAFAFB]">
    <div className="h-8 w-8 rounded-full border-[3px] border-[#0052FF] border-t-transparent animate-spin" />
  </div>
);

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Spinner />;
  if (user === false) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
};

const ProtectedBare = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Spinner />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  const location = useLocation();
  // Emergent Google OAuth returns to {origin}/#session_id=... — intercept before any route renders
  if (location.hash?.includes("session_id=")) return <GoogleCallback />;
  return (
    <Routes>
      <Route path="/" element={user === false ? <Landing /> : <Protected><Overview /></Protected>} />
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <AuthPage />} />
      <Route path="/signup" element={user ? <Navigate to="/onboarding" replace /> : <AuthPage initialMode="register" />} />
      <Route path="/onboarding" element={<ProtectedBare><Onboarding /></ProtectedBare>} />
      <Route path="/privacy" element={<Legal kind="privacy" />} />
      <Route path="/terms" element={<Legal kind="terms" />} />
      <Route path="/admin" element={<AdminPortal />} />
      <Route path="/support" element={<Protected><Support /></Protected>} />
      <Route path="/roadmap" element={<Protected><Roadmap /></Protected>} />
      <Route path="/campaigns" element={<Protected><Campaigns /></Protected>} />
      <Route path="/audiences" element={<Protected><Audiences /></Protected>} />
      <Route path="/ads" element={<Protected><Ads /></Protected>} />
      <Route path="/team-members" element={<Protected><TeamMembers /></Protected>} />
      <Route path="/activity" element={<Protected><ActivityLog /></Protected>} />
      <Route path="/creatives" element={<Navigate to="/ads" replace />} />
      <Route path="/leads" element={<Protected><Leads /></Protected>} />
      <Route path="/forms" element={<Protected><LeadForms /></Protected>} />
      <Route path="/assessment" element={<Protected><LeadAssessment /></Protected>} />
      <Route path="/conversion" element={<Protected><ConversionReport /></Protected>} />
      <Route path="/integrations" element={<Protected><Integrations /></Protected>} />
      <Route path="/connect" element={<Protected><Connect /></Protected>} />
      <Route path="/automation" element={<Protected><Automation /></Protected>} />
      <Route path="/team" element={<Protected><Team /></Protected>} />
      <Route path="/brand" element={<Protected><Brand /></Protected>} />
      <Route path="/billing" element={<Protected><Billing /></Protected>} />
      <Route path="/insights" element={<Protected><Insights /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
